import React, { useMemo, useRef, useState } from 'react';
import { Button, Tooltip, message } from 'antd';
import { CloseOutlined, CopyOutlined, PrinterOutlined } from '@ant-design/icons';
import { extractChaproomOrderId } from '../../../company/features';

const toFaDigits = (value) =>
  String(value ?? '').replace(/\d/g, (d) => '۰۱۲۳۴۵۶۷۸۹'[Number(d)]);

const escapeHtml = (value) =>
  String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');

const normalizeKey = (value) =>
  String(value || '')
    .trim()
    .toLowerCase()
    .replace(/[\s_\-]+/g, '');

const getTaskCustomFieldList = (task) => {
  const group = task?.custom_fields;
  let base = [];
  if (Array.isArray(group)) base = group;
  else if (Array.isArray(group?.fields)) base = group.fields;
  else if (Array.isArray(group?.custom_field)) base = group.custom_field;

  const orderFields = Array.isArray(group?.order_fields)
    ? group.order_fields
    : Array.isArray(task?.order_fields)
      ? task.order_fields
      : [];

  // order_fields چاپروم را هم در استخراج در نظر بگیر
  return [...orderFields, ...base];
};

const findFieldValue = (fields, matchers = []) => {
  const normalizedMatchers = matchers.map(normalizeKey);
  for (const field of fields) {
    const candidates = [field.label, field.name, field.title, field.key]
      .filter(Boolean)
      .map(normalizeKey);
    if (candidates.some((c) => normalizedMatchers.includes(c))) {
      const raw = field.value;
      if (raw == null) return '';
      const text = String(raw).trim();
      if (!text || text === 'null' || text === 'undefined') return '';
      return text;
    }
  }
  return '';
};

const parseHeadingMeta = (heading) => {
  const text = String(heading || '');
  const orderMatch = text.match(/#\s*(\d+)/) || text.match(/سفارش\s*#?\s*(\d+)/i);
  const nameMatch = text.match(/[-–—]\s*(.+)$/);
  return {
    orderId: orderMatch?.[1] || '',
    recipientName: nameMatch?.[1]?.trim() || '',
  };
};

export const extractChaproomPostalInfo = (task) => {
  const fields = getTaskCustomFieldList(task);
  const fromHeading = parseHeadingMeta(task?.heading);

  // اولویت با order_fields.order_id (custom_fields)
  const orderId =
    extractChaproomOrderId(task) ||
    findFieldValue(fields, ['orderId', 'orderid', 'order_id', 'شمارهسفارش', 'شسفارش']) ||
    fromHeading.orderId;

  const province = findFieldValue(fields, [
    'recipientDeliveryProvince',
    'province',
    'استان',
  ]);
  const city = findFieldValue(fields, ['recipientDeliveryCity', 'city', 'شهر']);
  const street = findFieldValue(fields, [
    'recipientDeliveryAddress',
    'recipientAddress',
    'address',
    'آدرس',
    'addressline',
  ]);
  const addressParts = [province, city, street].filter(Boolean);
  const address =
    addressParts.length > 0
      ? addressParts.join(' - ')
      : findFieldValue(fields, ['fullAddress', 'deliveryAddress']);

  const recipientName =
    findFieldValue(fields, [
      'recipientName',
      'recipientDeliveryName',
      'receiverName',
      'نامگیرنده',
      'گیرنده',
    ]) || fromHeading.recipientName;

  const mobile = findFieldValue(fields, [
    'recipientMobile',
    'recipientPhone',
    'mobile',
    'phone',
    'موبایل',
    'تلفن',
  ]);

  const postalCode = findFieldValue(fields, [
    'recipientPostalCode',
    'postalCode',
    'postal_code',
    'zipCode',
    'کدپستی',
  ]);

  return { orderId, address, recipientName, mobile, postalCode };
};

const buildPostalPlainText = (info) => {
  const orderLine = info.orderId ? `ش. سفارش #${toFaDigits(info.orderId)}` : 'ش. سفارش';
  return [
    orderLine,
    '',
    'آدرس',
    info.address || '—',
    '',
    'نام گیرنده',
    toFaDigits(info.recipientName || '—'),
    '',
    'موبایل',
    toFaDigits(info.mobile || '—'),
    '',
    'کدپستی',
    toFaDigits(info.postalCode || '—'),
  ].join('\n');
};

const buildPostalLabelHtml = (info) => {
  const orderTitle = info.orderId
    ? `ش. سفارش #${toFaDigits(info.orderId)}`
    : 'ش. سفارش';
  const rows = [
    ['آدرس', toFaDigits(info.address || '—')],
    ['نام گیرنده', toFaDigits(info.recipientName || '—')],
    ['موبایل', toFaDigits(info.mobile || '—')],
    ['کدپستی', toFaDigits(info.postalCode || '—')],
  ];

  return `<!doctype html>
<html lang="fa" dir="rtl">
<head>
  <meta charset="utf-8" />
  <title>${escapeHtml(orderTitle)}</title>
  <style>
    @page { size: auto; margin: 6mm; }
    * { box-sizing: border-box; }
    html, body {
      margin: 0;
      padding: 0;
      background: #fff;
      color: #111;
      direction: rtl;
      font-family: Tahoma, "Segoe UI", Arial, sans-serif;
    }
    .sheet {
      width: 100mm;
      min-height: 70mm;
      margin: 0 auto;
      border: 2px solid #111;
      border-radius: 6px;
      padding: 7mm 6mm 6mm;
    }
    .title {
      font-size: 15px;
      font-weight: 800;
      margin: 0 0 5mm;
      text-align: right;
      letter-spacing: -0.2px;
    }
    .row {
      display: flex;
      gap: 4mm;
      align-items: flex-start;
      padding: 2.2mm 0;
      border-top: 1px dashed #d1d5db;
    }
    .row:first-of-type { border-top: 0; padding-top: 0; }
    .label {
      width: 22mm;
      flex-shrink: 0;
      color: #6b7280;
      font-size: 11px;
      font-weight: 600;
      padding-top: 1px;
    }
    .value {
      flex: 1;
      font-size: 12.5px;
      font-weight: 700;
      line-height: 1.7;
      white-space: pre-wrap;
      word-break: break-word;
    }
    @media print {
      .sheet { border-color: #000; }
    }
  </style>
</head>
<body>
  <div class="sheet">
    <div class="title">${escapeHtml(orderTitle)}</div>
    ${rows
      .map(
        ([label, value]) =>
          `<div class="row"><div class="label">${escapeHtml(label)}</div><div class="value">${escapeHtml(value)}</div></div>`,
      )
      .join('')}
  </div>
</body>
</html>`;
};

const PostalRow = ({ label, value, multiline = false }) => (
  <div className={`flex gap-4 ${multiline ? 'items-start' : 'items-center'} py-2.5`}>
    <div
      className="w-[88px] shrink-0 text-[13px] text-gray-400"
      style={{ fontFamily: 'CustomFont' }}
    >
      {label}
    </div>
    <div
      className={`flex-1 text-[13px] text-gray-800 dark:text-gray-100 ${
        multiline ? 'leading-7' : 'leading-5'
      }`}
      style={{ fontFamily: 'CustomFont' }}
    >
      {value || '—'}
    </div>
  </div>
);

const ChaproomPostalInfoBox = ({ task, onClose }) => {
  const printRef = useRef(null);
  const [hidden, setHidden] = useState(false);

  const info = useMemo(() => extractChaproomPostalInfo(task), [task]);

  const hasAny =
    info.orderId || info.address || info.recipientName || info.mobile || info.postalCode;

  if (hidden || !hasAny) return null;

  const plainText = buildPostalPlainText(info);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(plainText);
      message.success('اطلاعات پستی کپی شد');
    } catch {
      message.error('کپی انجام نشد');
    }
  };

  const handlePrint = () => {
    // بدون window.open تا بلاکر پاپ‌آپ مانع نشود
    let iframe = document.getElementById('chaproom-postal-print-frame');
    if (!iframe) {
      iframe = document.createElement('iframe');
      iframe.id = 'chaproom-postal-print-frame';
      iframe.setAttribute('aria-hidden', 'true');
      iframe.style.cssText =
        'position:fixed;right:0;bottom:0;width:0;height:0;border:0;opacity:0;pointer-events:none;';
      document.body.appendChild(iframe);
    }

    const doc = iframe.contentDocument || iframe.contentWindow?.document;
    if (!doc || !iframe.contentWindow) {
      message.error('امکان آماده‌سازی چاپ وجود ندارد');
      return;
    }

    doc.open();
    doc.write(buildPostalLabelHtml(info));
    doc.close();

    const triggerPrint = () => {
      try {
        iframe.contentWindow.focus();
        iframe.contentWindow.print();
      } catch (err) {
        console.error(err);
        message.error('خطا در باز کردن دیالوگ چاپ');
      }
    };

    // صبر کوتاه برای رندر فونت/لیبل
    if (iframe.contentDocument?.readyState === 'complete') {
      setTimeout(triggerPrint, 50);
    } else {
      iframe.onload = () => setTimeout(triggerPrint, 50);
    }
  };

  return (
    <div
      ref={printRef}
      className="relative rounded-2xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 shadow-sm px-5 pt-4 pb-4"
      style={{ fontFamily: 'CustomFont', maxWidth: 420 }}
    >
      <button
        type="button"
        aria-label="بستن"
        className="absolute left-3 top-3 w-7 h-7 rounded-full bg-gray-900 text-white flex items-center justify-center hover:bg-gray-700 transition-colors border-0 cursor-pointer"
        onClick={() => {
          setHidden(true);
          onClose?.();
        }}
      >
        <CloseOutlined style={{ fontSize: 12 }} />
      </button>

      <div className="text-[15px] font-bold text-gray-800 dark:text-gray-100 mb-3 pr-1">
        {info.orderId ? `ش. سفارش #${toFaDigits(info.orderId)}` : 'ش. سفارش'}
      </div>

      <div className="divide-y divide-gray-100 dark:divide-gray-700">
        <PostalRow label="آدرس" value={toFaDigits(info.address)} multiline />
        <PostalRow label="نام گیرنده" value={toFaDigits(info.recipientName)} />
        <PostalRow label="موبایل" value={toFaDigits(info.mobile)} />
        <PostalRow label="کدپستی" value={toFaDigits(info.postalCode)} />
      </div>

      <div className="flex items-center justify-end gap-2 mt-4 pt-1">
        <Tooltip title="پرینت لیبل پستی">
          <Button
            type="text"
            icon={<PrinterOutlined style={{ color: '#14b8a6', fontSize: 18 }} />}
            onClick={handlePrint}
            className="w-9 h-9 flex items-center justify-center"
          />
        </Tooltip>
        <Tooltip title="کپی">
          <Button
            type="text"
            icon={<CopyOutlined style={{ color: '#22c55e', fontSize: 18 }} />}
            onClick={handleCopy}
            className="w-9 h-9 flex items-center justify-center"
          />
        </Tooltip>
      </div>
    </div>
  );
};

export default ChaproomPostalInfoBox;
